import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, Check, ChevronDown } from "lucide-react";
import { SUPPORTED } from "../i18n";

/**
 * LanguageSelector — sélecteur de langue PUBLIC (Phase 16).
 * Jamais monté dans le back-office. Langue courante visible, un clic pour ouvrir,
 * choix mémorisé (localStorage via i18next detector). Accessible (aria).
 *
 * variant: "light" (sur fond sombre, ex. Coming Soon/footer) | "dark" (sur fond clair, navbar).
 */
export default function LanguageSelector({ variant = "dark", className = "" }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = SUPPORTED.find((s) => s.code === i18n.language) || SUPPORTED[0];

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const choose = (code) => {
    i18n.changeLanguage(code); // met en cache localStorage → prévaut sur la détection
    setOpen(false);
  };

  const light = variant === "light";
  const triggerCls = light
    ? "text-white/80 hover:text-white border-white/20 hover:bg-white/10"
    : "text-primary/80 hover:text-primary border-border hover:bg-muted";

  return (
    <div className={`relative ${className}`} ref={ref} data-testid="language-selector">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("lang.select")}
        data-testid="language-selector-trigger"
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${triggerCls}`}
      >
        <Globe className="h-4 w-4" aria-hidden="true" />
        <span data-testid="language-current">{current.short}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {open && (
        <ul
          role="listbox"
          aria-label={t("lang.label")}
          data-testid="language-selector-menu"
          className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-border bg-white py-1 shadow-lg"
        >
          {SUPPORTED.map((s) => {
            const active = s.code === current.code;
            return (
              <li key={s.code} role="option" aria-selected={active}>
                <button
                  type="button"
                  onClick={() => choose(s.code)}
                  data-testid={`language-option-${s.code}`}
                  className={`flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors hover:bg-muted ${active ? "font-semibold text-primary" : "text-foreground"}`}
                >
                  <span>{s.label}</span>
                  {active && <Check className="h-4 w-4 text-accent" aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
